import { useEffect, useState } from 'react'
import { Box, Group, Text } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import agent from '../../app/api/agent'
import type { ContentPhotoDto } from '../../app/models/contentPhoto'
import { ContentPhotoOwnerType } from '../../app/models/contentPhoto'
import type { Part } from '../../app/models/part'
import { useStore } from '../../app/stores/store'
import PhotoGallery from '../builds/PhotoGallery'
import GalleryUploadButton from '../builds/GalleryUploadButton'

type Props = {
  part: Part;
  onPhotosChanged?: () => void;
};

/** Supplemental gallery for an approved part; any logged-in user may upload (server validates). */
export default observer(function PartPhotoGallerySection ({ part, onPhotosChanged }: Props) {
  const { userStore } = useStore()
  const [photos, setPhotos] = useState<ContentPhotoDto[]>([])
  const [loading, setLoading] = useState(true)

  const canUpload = userStore.isLoggedIn && (part.status === 1 || userStore.isAdmin)
  const isAuthor = userStore.user?.username === part.authorUsername
  const canSetMain = !!(isAuthor || userStore.isAdmin || userStore.isModerator)
  const canDeletePhoto = (p: ContentPhotoDto) =>
    !!(userStore.isAdmin || (p.uploadedByUsername != null && userStore.user?.username === p.uploadedByUsername))

  const reload = async () => {
    const list = await agent.ContentPhotos.list(ContentPhotoOwnerType.Part, part.id)
    setPhotos(list)
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    agent.ContentPhotos.list(ContentPhotoOwnerType.Part, part.id)
      .then((list) => {
        if (!cancelled) setPhotos(list)
      })
      .catch(() => {
        if (!cancelled) setPhotos([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [part.id])

  const handleDelete = async (id: string) => {
    await agent.ContentPhotos.delete(id)
    setPhotos((prev) => prev.filter((x) => x.id !== id))
    await reload()
    onPhotosChanged?.()
  }

  const handleSetMain = async (id: string) => {
    await agent.ContentPhotos.setMain(id)
    await reload()
    onPhotosChanged?.()
  }

  const handleUploadSuccess = (photo: ContentPhotoDto) => {
    setPhotos((prev) => [...prev, photo])
    onPhotosChanged?.()
  }

  if (!loading && photos.length === 0 && !canUpload) {
    return null
  }

  return (
    <Box sx={{ marginTop: 16 }}>
      <Group position="apart" align="center" mb="sm" sx={{ flexWrap: 'wrap' }}>
        <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 14, color: 'var(--gs-text)' }}>
          COMMUNITY PHOTOS
        </Text>
        {canUpload && (
          <GalleryUploadButton
            ownerType={ContentPhotoOwnerType.Part}
            ownerId={part.id}
            canUpload={canUpload}
            onSuccess={handleUploadSuccess}
            label="Add photo"
          />
        )}
      </Group>
      {loading
        ? (
        <Text size="sm" sx={{ color: 'var(--gs-muted)', fontFamily: 'DM Sans, sans-serif' }}>
          Loading photos…
        </Text>
          )
        : photos.length > 0
          ? (
        <PhotoGallery
          photos={photos}
          canManage={userStore.isLoggedIn}
          variant="part"
          canSetMain={canSetMain}
          canDeletePhoto={canDeletePhoto}
          onDelete={handleDelete}
          onSetMain={handleSetMain}
        />
            )
          : canUpload
            ? (
        <Text size="sm" color="dimmed" sx={{ fontFamily: 'DM Sans, sans-serif' }}>
          No community photos yet. Add different configurations of this part.
        </Text>
              )
            : null}
    </Box>
  )
})
